import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowDownAZ, Filter as FilterIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SORT_KEYS, FILTER_KEYS } from "@/lib/galleryUtils";

/**
 * Mobile-only compact control row: primary sort + secondary sort +
 * filter popover. Rendered above the gallery grid on screens < md.
 *
 * All state lives in Gallery.jsx — this component is a pure presentational
 * shell so behavior stays byte-for-byte identical to the pre-split
 * inline JSX.
 */
export const GalleryMobileControls = ({
  sortKey,
  setSortAndPersist,
  sortKey2,
  setSortKey2,
  secondaryOptions,
  filters,
  filterCounts,
  toggleFilter,
  clearFilters,
  noFiltersActive,
}) => {
  const { t } = useTranslation();

  const activeFilterLabel = noFiltersActive
    ? t("gallery.filterLabel")
    : filters.size === 1
      ? t(`gallery.filters.${Array.from(filters)[0]}`)
      : t("gallery.filterActive", { n: filters.size });

  return (
    <div className="px-5 mt-3 md:hidden" data-testid="gallery-mobile-controls">
      {/* Single-row layout — flex-nowrap + horizontal-scroll so the three
          pills stay side-by-side even when the user has bumped up their
          text-size preference or picked a long language string. Users
          almost never need Filter + primary + secondary at once, so the
          rightmost pill being a swipe away costs nothing. */}
      <div className="flex items-center gap-2 flex-nowrap overflow-x-auto no-scrollbar -mx-1 px-1">
        <Select value={sortKey} onValueChange={(v) => setSortAndPersist(v)}>
          <SelectTrigger
            data-testid="sort-select-mobile"
            aria-label={t("gallery.sort.label")}
            className="shrink-0 h-9 w-auto rounded-full border-[#E8E5DF] bg-white text-leaf-ink text-xs px-3 gap-1.5"
          >
            <ArrowDownAZ className="w-3.5 h-3.5 text-leaf-mist shrink-0" strokeWidth={1.5} />
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start">
            {SORT_KEYS.map((k) => (
              <SelectItem key={k} value={k} data-testid={`sort-option-mobile-${k}`}>
                {t(`gallery.sort.options.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortKey2} onValueChange={setSortKey2}>
          <SelectTrigger
            data-testid="sort-select-secondary-mobile"
            aria-label={t("gallery.sort.secondaryLabel")}
            className={`shrink-0 h-9 w-auto rounded-full text-xs px-3 gap-1.5 transition ${
              sortKey2 === "none"
                ? "border-[#E8E5DF] bg-white text-leaf-mist"
                : "border-leaf/40 bg-[#E6EFE9] text-leaf-deep"
            }`}
          >
            <span>
              {sortKey2 === "none"
                ? t("gallery.sort.thenBy")
                : t("gallery.sort.thenByShort", { label: t(`gallery.sort.options.${sortKey2}`) })}
            </span>
          </SelectTrigger>
          <SelectContent align="start">
            {secondaryOptions.map((k) => (
              <SelectItem key={k} value={k} data-testid={`sort-option-secondary-mobile-${k}`}>
                {k === "none" ? t("gallery.sort.secondaryNone") : t(`gallery.sort.options.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 shrink-0">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="filter-select-mobile"
                aria-label={t("gallery.filterLabel")}
                className={`h-9 rounded-full text-xs px-3 gap-1.5 inline-flex items-center border transition shrink-0 ${
                  noFiltersActive
                    ? "border-[#E8E5DF] bg-white text-leaf-ink"
                    : "border-leaf/40 bg-[#E6EFE9] text-leaf-deep"
                }`}
              >
                <FilterIcon
                  className={`w-3.5 h-3.5 shrink-0 ${noFiltersActive ? "text-leaf-mist" : "text-leaf"}`}
                  strokeWidth={1.5}
                />
                {/* When no filters are active, show the icon alone — the
                    aria-label still names the control for AT users. Once
                    a filter is on, the label surfaces the active count
                    so the state is visible at a glance. */}
                {!noFiltersActive && (
                  <span className="truncate">{activeFilterLabel}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-2" data-testid="filter-options-mobile">
              <div className="max-h-72 overflow-y-auto">
                {FILTER_KEYS.filter((k) => k !== "all").map((key) => {
                  const checked = filters.has(key);
                  return (
                    <label
                      key={key}
                      data-testid={`filter-option-mobile-${key}`}
                      className="flex items-center gap-2 px-2 py-2 rounded hover:bg-parchment cursor-pointer text-sm text-leaf-ink"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleFilter(key)}
                        aria-label={t(`gallery.filters.${key}`)}
                      />
                      <span className="flex-1">{t(`gallery.filters.${key}`)}</span>
                      <span className="text-[10px] text-leaf-mist [font-variant-numeric:lining-nums_tabular-nums]">
                        {filterCounts[key] ?? 0}
                      </span>
                    </label>
                  );
                })}
              </div>
              {!noFiltersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  data-testid="filter-clear-all-mobile"
                  className="w-full mt-1 px-2 py-2 text-xs text-leaf-mist hover:text-leaf border-t border-[#EFEBE3] transition"
                >
                  {t("gallery.filterClearAllLabel")}
                </button>
              )}
            </PopoverContent>
          </Popover>
          {!noFiltersActive && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="filter-clear-mobile"
              aria-label={t("gallery.filterClearAria")}
              className="shrink-0 w-7 h-7 rounded-full bg-white border border-[#E8E5DF] text-leaf-mist hover:text-leaf hover:border-leaf/40 flex items-center justify-center transition"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GalleryMobileControls;
